import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import fetchSyncedBillsService from "../services/fetchSyncedBills";

// Sentinel value used to signal "request was canceled by the axios dedup
// interceptor" so reducers can no-op instead of surfacing it as an error toast.
const CANCELED_SENTINEL = "__CANCELED__";
import type { Bill, BillFilters, BillsState } from "../views/BillManagement/types";

const initialState: BillsState = {
    bills: [],
    loading: false,
    error: null,
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    linkedCount: 0,
    notLinkedCount: 0,
    hasMore: true,
    filters: {},
    shopId: null,
};

// Async thunk to fetch bills
export const fetchBills = createAsyncThunk(
    "bills/fetchBills",
    async (
        {
            shopId,
            page = 1,
            limit = 40,
            filters = {},
            append = false,
        }: {
            shopId: string;
            page?: number;
            limit?: number;
            filters?: BillFilters;
            append?: boolean;
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await fetchSyncedBillsService.fetchSyncedBills(shopId, {
                page,
                limit,
                filters,
            });

            // Extract result.data from response (matching orders pattern)
            const data = response.data.result.data;

            return {
                bills: data.bills,
                totalItems: data.totalItems,
                totalPages: data.totalPages,
                currentPage: data.currentPage,
                linkedCount: data.linkedCount || 0,
                notLinkedCount: data.notLinkedCount || 0,
                append,
            };
        } catch (error: any) {
            // Axios in-flight dedup: don't surface as a real error.
            if (axios.isCancel(error) || error?.name === "CanceledError") {
                return rejectWithValue(CANCELED_SENTINEL);
            }
            return rejectWithValue(error.message || "Failed to fetch bills");
        }
    }
);

const billsSlice = createSlice({
    name: "bills",
    initialState,
    reducers: {
        setShopId: (state, action: PayloadAction<string>) => {
            state.shopId = action.payload;
        },
        setFilters: (state, action: PayloadAction<BillFilters>) => {
            state.filters = action.payload;
            // Reset pagination when filters change
            state.currentPage = 1;
            state.bills = [];
            state.hasMore = true;
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },
        resetBills: (state) => {
            state.bills = [];
            state.currentPage = 1;
            state.hasMore = true;
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        prependBill: (state, action: PayloadAction<Bill>) => {
            // Check if bill already exists (avoid duplicates)
            const exists = state.bills.some(
                (bill) => bill.Id === action.payload.Id && bill.InvoiceNo === action.payload.InvoiceNo
            );
            if (!exists) {
                // Add new bill at the beginning
                state.bills = [action.payload, ...state.bills];
                // Update counts
                state.totalItems += 1;
                if (action.payload.linkedOrderId) {
                    state.linkedCount += 1;
                } else {
                    state.notLinkedCount += 1;
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBills.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBills.fulfilled, (state, action) => {
                state.loading = false;
                const { bills, totalItems, totalPages, currentPage, linkedCount, notLinkedCount, append } = action.payload;

                if (append) {
                    // Append for infinite scroll
                    state.bills = [...state.bills, ...bills];
                } else {
                    // Replace for fresh fetch
                    state.bills = bills;
                }

                state.totalItems = totalItems;
                state.totalPages = totalPages;
                state.currentPage = currentPage;
                state.linkedCount = linkedCount;
                state.notLinkedCount = notLinkedCount;
                state.hasMore = currentPage < totalPages;
            })
            .addCase(fetchBills.rejected, (state, action) => {
                // Canceled by the axios dedup interceptor — the canceled
                // request was a duplicate; the original is still in flight
                // and will settle via fulfilled/rejected shortly. Leave
                // `state.loading` untouched so the Table's loading overlay
                // stays visible until the real request completes.
                if (action.payload === CANCELED_SENTINEL) {
                    return;
                }
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    setShopId,
    setFilters,
    setCurrentPage,
    resetBills,
    clearError,
    prependBill,
} = billsSlice.actions;

export default billsSlice.reducer;
